import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import "./SkillPage.css";
import BackButton from "../../components/BackButton/BackButton";

/* ── helpers ─────────────────────────────────────────────── */
const extractVideoId = (url = "") => {
  const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
};

const getThumbnail = (url = "") => {
  const vid = extractVideoId(url);
  return vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : null;
};

const isVideo    = (url = "") => /(?:v=|youtu\.be\/)/.test(url) && !/[?&]list=/.test(url);
const isPlaylist = (url = "") => /[?&]list=/.test(url);

const getType = (r) => {
  if (r.type) return r.type;
  if (isVideo(r.url))    return "video";
  if (isPlaylist(r.url)) return "playlist";
  return "link";
};

const TYPE_META = {
  video:    { icon: "🎬", label: "Video",    color: "#2563eb" },
  playlist: { icon: "📋", label: "Playlist", color: "#7c3aed" },
  link:     { icon: "🔗", label: "Resource", color: "#059669" },
};

/* ── Component ───────────────────────────────────────────── */
export default function SkillPage() {
  const { roadmapId }  = useParams();
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const itemId         = searchParams.get("itemId");

  const [skill,   setSkill]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/roadmap/${roadmapId}/items/${itemId}`);
        setSkill(res.data.project); // controller returns field named "project" for all items
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (itemId) fetch();
    else setLoading(false);
  }, [roadmapId, itemId]);

  // Re-fetch when user returns to this tab
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && itemId) {
        api.get(`/roadmap/${roadmapId}/items/${itemId}`)
          .then(res => setSkill(res.data.project))
          .catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [roadmapId, itemId]);

  /* ── states ───────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="sp-page sp-page--center">
        <div className="sp-spinner" />
        <p>Loading skill…</p>
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="sp-page sp-page--center">
        <div className="sp-empty-icon">🎯</div>
        <h2>Skill not found</h2>
        <BackButton />
      </div>
    );
  }

  const sources  = skill.resources?.length ? skill.resources : (skill.playlists || []);
  const filtered = sources.filter(r => r.url?.trim());

  /* ── render ───────────────────────────────────────────── */
  return (
    <div className="sp-page">
      <div className="sp-container">

        <BackButton to={`/roadmap/${roadmapId}`} label="Back to Roadmap" />

        <div className="sp-card">

          {/* Header */}
          <div className="sp-header">
            <div className="sp-type-badge">🎯 Skill</div>
            <h1 className="sp-title">{skill.name}</h1>
            <div className="sp-count">
              {filtered.length} resource{filtered.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Detached banner — shown if any resource is removed */}
          {filtered.some(r => r.detached) && (
            <div className="sp-detached-banner">
              <span className="sp-detached-banner-icon">⚠️</span>
              <div>
                <strong>Course removed from your library</strong>
                <p>
                  One or more courses in this skill were deleted. Progress tracking is paused for those resources.
                  Re-add the course below to restore tracking — your roadmap will automatically re-link.
                  <br/>
                  <em>Note: re-adding creates fresh progress. Previous watch history will not be restored.</em>
                </p>
              </div>
            </div>
          )}

          {/* Resources list */}
          {filtered.length === 0 ? (
            <div className="sp-empty">
              <span>No resources added yet.</span>
              <p>Edit this skill from the roadmap to add YouTube playlists or videos.</p>
            </div>
          ) : (
            <div className="sp-resources">
              {filtered.map((r, i) => {
                const type  = getType(r);
                const meta  = TYPE_META[type] || TYPE_META.link;
                const thumb = getThumbnail(r.url);
                const ytUrl = r.url.startsWith("http") ? r.url : `https://${r.url}`;

                const hasCourse = !!r.course;
                const isDetached  = r.detached === true;

                return (
                  <div key={i} className={`sp-resource sp-resource--${type} ${isDetached ? "sp-resource--detached" : ""}`}>

                    {/* Thumbnail */}
                    <div className="sp-thumb-wrap">
                      {thumb && !isDetached ? (
                        <img src={thumb} alt="" className="sp-thumb" />
                      ) : (
                        <div className="sp-thumb-placeholder">
                          {isDetached ? "⚠️" : meta.icon}
                        </div>
                      )}
                      <span className="sp-type-chip" style={{ background: isDetached ? "#ef4444" : meta.color }}>
                        {isDetached ? "Removed" : meta.label}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="sp-resource-info">
                      <span className="sp-resource-url">{r.url}</span>

                      {isDetached && (
                        <span className="sp-detached-label">
                          ⚠ This course was removed from your library — re-add it to restore progress tracking.
                        </span>
                      )}

                      {/* Action buttons */}
                      <div className="sp-resource-actions">
                        {hasCourse && !isDetached && (
                          <button
                            className="sp-btn sp-btn--course"
                            onClick={() => navigate(`/course/${r.course._id || r.course}`)}
                          >
                            Open Course →
                          </button>
                        )}
                        {isDetached && r.url && (
                          <button
                            className="sp-btn sp-btn--readd"
                            onClick={() => navigate("/add-course", {
                              state: isVideo(r.url)
                                ? { videoUrl: r.url }
                                : { playlistUrl: r.url }
                            })}
                          >
                            + Re-add Course
                          </button>
                        )}
                        <a
                          href={ytUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="sp-btn sp-btn--yt"
                        >
                          ↗ YouTube
                        </a>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}